import { Clock, Pause, Play, Settings } from 'lucide-react-native';
import { useState } from 'react';
import {
  Platform,
  Pressable,
  StyleSheet,
  useWindowDimensions,
  View,
  type ViewStyle,
} from 'react-native';
import { Text, VStack } from '../../../components/primitives';
import { useTranslation } from '../../../contexts/TranslationContext';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import type { Workflow } from './WorkflowCard';

interface WorkflowListItemProps {
  workflow: Workflow;
  onPress?: () => void;
  onRun?: () => void;
  onTogglePause?: () => void;
  onEdit?: () => void;
  numColumns?: number;
}

const statusConfig: Record<
  'active' | 'paused' | 'error',
  { color: string; bg: string; labelKey: string }
> = {
  active: {
    color: colors.status.success.main,
    bg: colors.status.success.bg,
    labelKey: 'workflow.card.status.active',
  },
  paused: {
    color: colors.status.warning.main,
    bg: colors.status.warning.bg,
    labelKey: 'workflow.card.status.paused',
  },
  error: {
    color: colors.status.error.main,
    bg: colors.status.error.bg,
    labelKey: 'workflow.card.status.error',
  },
};

export function WorkflowListItem({
  workflow,
  onPress,
  onRun,
  onTogglePause,
  onEdit,
  numColumns = 1,
}: Readonly<WorkflowListItemProps>) {
  const { t } = useTranslation();
  const [isHovered, setIsHovered] = useState(false);
  const { width: windowWidth } = useWindowDimensions();

  const isNative = Platform.OS !== 'web';
  const isDisabled = workflow.disabled === true;
  const status = statusConfig[workflow.status];

  // Mirror LeadListItem's explicit-width strategy: iOS Yoga doesn't reliably
  // propagate the FlatList row constraint to a Pressable, so we compute width
  // from the viewport. Web uses flex sizing.
  const cardWidth = (() => {
    if (!isNative) return undefined;
    const outerPad = 48;
    const interColumnGap = (numColumns - 1) * tokens.spacing.md;
    const perColumn = (windowWidth - outerPad - interColumnGap) / numColumns;
    return Math.max(perColumn, 280);
  })();
  const bodyWidth =
    isNative && cardWidth !== undefined
      ? cardWidth - tokens.spacing.md * 2 - 42 - tokens.spacing.md
      : undefined;

  const webHoverStyle = Platform.select<ViewStyle>({
    web: {
      transition: 'border-color 0.15s ease, background-color 0.15s ease',
      cursor: isDisabled ? 'default' : 'pointer',
    } as ViewStyle,
    default: {},
  });

  const hoverProps = Platform.select({
    web: {
      onMouseEnter: () => setIsHovered(true),
      onMouseLeave: () => setIsHovered(false),
    },
    default: {},
  });

  const isActive = !isDisabled && isHovered;

  return (
    <Pressable
      testID={`workflow_item_${workflow.id}`}
      onPress={isDisabled ? undefined : onPress}
      style={styles.pressable}
    >
      <View
        style={[
          styles.card,
          isNative ? { width: cardWidth } : styles.cardWeb,
          isActive && styles.cardActive,
          isDisabled && styles.cardDisabled,
          webHoverStyle,
        ]}
        {...hoverProps}
      >
        <View style={styles.row}>
          <View style={styles.avatar}>{workflow.icon}</View>

          <VStack
            spacing="xs"
            style={[styles.body, isNative ? { width: bodyWidth } : styles.bodyWeb]}
          >
            <View style={[styles.titleRow, isNative && { width: bodyWidth }]}>
              <Text
                variant="body"
                color={colors.text.primary}
                style={[
                  styles.title,
                  isNative && bodyWidth !== undefined
                    ? { maxWidth: bodyWidth - 110 - tokens.spacing.sm }
                    : styles.titleWeb,
                ]}
                numberOfLines={1}
                ellipsizeMode="tail"
              >
                {workflow.name}
              </Text>

              {isDisabled ? (
                <View style={styles.statusPill}>
                  <Text style={styles.statusText} numberOfLines={1} ellipsizeMode="tail">
                    {t('workflow.card.comingSoon')}
                  </Text>
                </View>
              ) : (
                <View
                  style={[
                    styles.statusPill,
                    { backgroundColor: status.bg, borderColor: status.color },
                  ]}
                >
                  <View style={[styles.statusDot, { backgroundColor: status.color }]} />
                  <Text
                    style={[styles.statusText, { color: status.color }]}
                    numberOfLines={1}
                    ellipsizeMode="tail"
                  >
                    {t(status.labelKey)}
                  </Text>
                </View>
              )}
            </View>

            <Text
              variant="caption"
              color={colors.text.secondary}
              style={[styles.description, isNative && { width: bodyWidth }]}
              numberOfLines={2}
            >
              {workflow.description}
            </Text>

            <View style={[styles.footer, isNative && { width: bodyWidth }]}>
              <View style={styles.metaRow}>
                <View style={styles.metaItem}>
                  <Clock size={12} color={colors.text.tertiary} strokeWidth={1.8} />
                  <Text style={styles.metaText} numberOfLines={1}>
                    {workflow.lastRun || t('workflow.card.neverRun')}
                  </Text>
                </View>
                <Text style={styles.metaDivider}>·</Text>
                <Text style={styles.metaText}>
                  {workflow.executionCount} {t('workflow.card.executions')}
                </Text>
              </View>

              {!isDisabled && (onTogglePause || onRun || onEdit) && (
                <View style={styles.actions}>
                  {onTogglePause && (
                    <Pressable
                      onPress={(e) => {
                        e.stopPropagation();
                        onTogglePause();
                      }}
                      style={({ pressed }) => [
                        styles.actionBtn,
                        workflow.status === 'active' ? styles.actionPause : styles.actionRun,
                        pressed && styles.actionPressed,
                      ]}
                      accessibilityRole="button"
                      accessibilityLabel={
                        workflow.status === 'active'
                          ? t('workflow.card.pauseButton')
                          : t('workflow.card.resumeButton')
                      }
                    >
                      {workflow.status === 'active' ? (
                        <Pause size={13} color={colors.text.primary} strokeWidth={2} />
                      ) : (
                        <Play size={13} color={colors.primary[900]} strokeWidth={2.5} />
                      )}
                    </Pressable>
                  )}
                  {onRun && workflow.status === 'active' && (
                    <Pressable
                      onPress={(e) => {
                        e.stopPropagation();
                        onRun();
                      }}
                      style={({ pressed }) => [
                        styles.actionBtn,
                        styles.actionRun,
                        pressed && styles.actionPressed,
                      ]}
                      accessibilityRole="button"
                      accessibilityLabel={t('workflow.card.runButton')}
                    >
                      <Play size={13} color={colors.primary[900]} strokeWidth={2.5} />
                    </Pressable>
                  )}
                  {onEdit && (
                    <Pressable
                      onPress={(e) => {
                        e.stopPropagation();
                        onEdit();
                      }}
                      style={({ pressed }) => [
                        styles.actionBtn,
                        styles.actionEdit,
                        pressed && styles.actionPressed,
                      ]}
                      accessibilityRole="button"
                      accessibilityLabel={t('common.actions.edit') ?? 'Edit'}
                    >
                      <Settings size={13} color={colors.text.secondary} strokeWidth={1.8} />
                    </Pressable>
                  )}
                </View>
              )}
            </View>
          </VStack>
        </View>
      </View>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  pressable: {
    alignSelf: 'stretch',
    minHeight: 174,
    ...(Platform.OS === 'web' ? ({ width: '100%' } as ViewStyle) : {}),
  },
  card: {
    backgroundColor: colors.surface.row,
    borderRadius: tokens.radius.lg,
    borderWidth: 1,
    borderColor: colors.border.default,
    padding: tokens.spacing.md,
    overflow: 'hidden',
    minHeight: 174,
  },
  cardWeb: {
    width: '100%',
    maxWidth: '100%',
  },
  cardActive: {
    borderColor: colors.primary.border,
  },
  cardDisabled: {
    opacity: 0.55,
  },
  row: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: tokens.spacing.md,
  },
  avatar: {
    width: 42,
    height: 42,
    borderRadius: 21,
    backgroundColor: colors.primary.muted,
    alignItems: 'center',
    justifyContent: 'center',
  },
  body: {
    flex: 1,
    minWidth: 0,
  },
  bodyWeb: {
    flex: 1,
    minWidth: 0,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  title: {
    fontWeight: '700',
    fontSize: tokens.fontSize.basePlus,
    marginRight: tokens.spacing.sm,
  },
  titleWeb: {
    flexShrink: 1,
    minWidth: 0,
  },
  statusPill: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: tokens.spacing.sm,
    paddingVertical: 3,
    borderRadius: 999,
    backgroundColor: colors.surface.row,
    borderWidth: 1,
    borderColor: colors.border.subtle,
    maxWidth: 110,
    flexShrink: 0,
  },
  statusDot: {
    width: 7,
    height: 7,
    borderRadius: 4,
    marginRight: 6,
  },
  statusText: {
    color: colors.text.secondary,
    fontSize: tokens.fontSize.xs,
    fontWeight: '600',
    letterSpacing: 0.2,
  },
  description: {
    flexGrow: 1,
    fontSize: tokens.fontSize.sm,
    marginTop: 2,
    lineHeight: 16,
  },
  footer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 6,
    gap: tokens.spacing.sm,
  },
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flexShrink: 1,
    minWidth: 0,
  },
  metaItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    flexShrink: 1,
    minWidth: 0,
  },
  metaText: {
    fontSize: tokens.fontSize.xs,
    color: colors.text.tertiary,
    flexShrink: 1,
  },
  metaDivider: {
    fontSize: tokens.fontSize.xs,
    color: colors.text.tertiary,
  },
  actions: {
    flexDirection: 'row',
    gap: 6,
    flexShrink: 0,
  },
  actionBtn: {
    width: 28,
    height: 28,
    borderRadius: tokens.radius.smPlus,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
  },
  actionRun: {
    backgroundColor: colors.primary[500],
    borderColor: colors.primary[500],
  },
  actionPause: {
    backgroundColor: colors.background.elevated,
    borderColor: colors.border.muted,
  },
  actionEdit: {
    backgroundColor: 'transparent',
    borderColor: colors.border.muted,
  },
  actionPressed: {
    opacity: 0.7,
  },
});
